// Import the RTK Query methods from the React-specific entry point
import { FetchBaseQueryMeta, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import JSON5 from 'json5';

import { CATMaterialRoot, CfgOptionsRoot, CommonElementsRoot, ElementsRoot, MenuCategory, MenuFamily, MenuVariant, Part } from './types/catalog-types'
import { APIMaterialRoot } from './types/catalog-internal';
import { LoginResponse, PricingRequest, PricingResponse, Bullet, CartItem, Cart, SaveState } from './types/api-types'
import { transformCommonElementsResponse, transformElementsResponse, transformMaterialsResponse, transformOptionsResponse, transformServerMaterialsResponse } from './api-helpers';
import { getBasket, getBasketLineComponents, getBasketLines, getCartBaskets, getCustomerCarts, getShoppingCart, makePricingRequestParams, printCart } from './api-client';
import { MSG2 } from '../messages';

const JSONCResponseHandler = async (response: { text: () => any, status: number }) => {
    const text = await response.text();
    
    // Handle empty responses (like 404 errors)
    if (!text || text.trim() === '') {
        throw new Error(`HTTP ${response.status}: Empty response body`);
    }
    
    try {
        return JSON5.parse(text);
    } catch (error) {
        throw new Error(`JSON5 parsing error: ${error instanceof Error ? error.message : 'Unknown error'}. Response: ${text.substring(0, 100)}...`);
    }
}

// Slice object
export const api = createApi({

    reducerPath: 'api',

    baseQuery: fetchBaseQuery({
        baseUrl: import.meta.env.VITE_API,
        responseHandler: JSONCResponseHandler
    }),

    endpoints: builder => ({

        getCategories: builder.query<MenuCategory[], string>({
            query: lang => '/categories.json',
            transformResponse: (response: unknown, meta: FetchBaseQueryMeta, arg: string) => {
                const out: MenuCategory[] = (response as any[]).map(c => {
                    return {
                        ...c,
                        name: c.name[arg],
                    }
                });
                return out;
            }
        }),

        getFamilies: builder.query<MenuFamily[], { lang: string, category: string }>({
            query: ({ lang, category }) => `/families.json`,
            transformResponse: (response: unknown, meta: FetchBaseQueryMeta, arg: { lang: string, category: string }) => {
                const out: MenuFamily[] = (response as any)[arg.category].map((f:any) => {
                    return {
                        ...f,
                        name: f.name[arg.lang]
                    }
                });
                return out;
            }
        }),

        getFamilyCategory: builder.query<string|undefined, string>({
            query: (familyId) => `/families.json`,
            transformResponse: (response: unknown, meta: FetchBaseQueryMeta, arg: string) => {
                const familyId = arg.toLowerCase();
                const categoryEntry = Object.entries(response as Record<string,any[]>).find(([categoryId, families]: [string,any[]]) => {
                    return families.some(f => {
                        return f.id.toLowerCase() === familyId
                    });                    
                })
                return categoryEntry ? categoryEntry[0] : undefined;
            }
        }),

        getFamily: builder.query<MenuFamily, { lang: string, familyId: string }>({
            query: ({ lang, familyId }) => `/families.json`,
            transformResponse: (response: unknown, meta: FetchBaseQueryMeta, arg: { lang: string, familyId: string }) => {
                for(const [catId, families] of Object.entries(response as Object)) {
                    for(const family of families) {
                        if(family.id === arg.familyId) {
                            return {
                                ...family,
                                name: family.name[arg.lang]
                            }            
                        }
                    }
                }
                throw new Error('Unknown family')
            }
        }),

        getVariants: builder.query<MenuVariant|undefined, { lang: string, family: string }>({
            query: ({ lang, family }) => `/variants.json`,
            transformResponse: (response: unknown, meta: FetchBaseQueryMeta, arg: { lang: string, family: string }) => {
                const variantsItem = (response as any)[arg.family];
                let variants;
                if(variantsItem) {
                    variants = {
                        title: variantsItem.title[arg.lang],
                        options: variantsItem.options.map((option:any) => {
                            return {
                                ...option,
                                title: option.title[arg.lang]
                            }
                        })
                    }    
                } else {
                    variants = undefined;
                }
                return variants;
            }
        }),

        getOptions: builder.query<CfgOptionsRoot, { lang: string, familyId: string, variantId?: string }>({
            query: ({ lang, familyId, variantId }) => {
                if(!familyId) {
                    throw new Error('No familyId provided');
                } else if(variantId) {
                    return `/${familyId}.${variantId}.options.json`
                } else {
                    return `/${familyId}.options.json`
                }
            },
            transformResponse: (response: unknown, meta: FetchBaseQueryMeta, arg: { lang: string, familyId: string, variantId?: string }) => {
                return transformOptionsResponse(response, arg.lang);
            }
        }),

        getCommonElements: builder.query<CommonElementsRoot, { lang: string }>({
            query: ({ lang }) => `/elements.json`,
            transformResponse: (response: unknown, meta: FetchBaseQueryMeta, arg: { lang: string, familyId: string, variantId?: string }) => {
                return transformCommonElementsResponse(response, arg.lang);
            }
        }),

        getElements: builder.query<ElementsRoot, { lang: string, familyId: string, variantId?: string }>({
            query: ({ lang, familyId, variantId }) => {
                if(!familyId) {
                    throw new Error('No familyId provided');
                } else if(variantId) {
                    return `/${familyId}.${variantId}.elements.json`
                } else {
                    return `/${familyId}.elements.json`
                }
            },
            transformResponse: (response: unknown, meta: FetchBaseQueryMeta, arg: { lang: string, familyId: string, variantId?: string }) => {
                return transformElementsResponse(response, arg.lang);
            }
        }),

        getParts: builder.query<Part[], { lang: string, familyId: string, variantId?: string }>({
            query: ({ lang, familyId, variantId }) => `/parts.json`
        }),

        getFamilyParts: builder.query<Part[], { lang: string, familyId: string, variantId?: string }>({
            query: ({ lang, familyId, variantId }) => {
                if(!familyId) {
                    throw new Error('No familyId provided');
                } else if(variantId) {
                    return `/${familyId}.${variantId}.parts.json`
                } else {
                    return `/${familyId}.parts.json`
                }
            }
        }),

        getMaterials: builder.query<CATMaterialRoot, string>({
            query: (lang) => `/materials.json`,
            transformResponse: (response: unknown, meta: FetchBaseQueryMeta, arg: string) => {
                return transformMaterialsResponse(response, arg);
            }
        }),

        // TODO temporary one while the get text service returns everything
        getMessages: builder.query<Record<string, string>, {lang: string, sessionId: string} >({            
            // TODO: this should be the good one, without the use of MSG2
            // (or keeping MSG2 only with new functionalities)
            query: ({lang, sessionId}) => ({
                url: `/messages.json`,
                method:'GET'     
            }),
            transformResponse: (response: any, _, args) => {
                const convertedObject: Record<string, string> = (response as any).JRCFGATXT.reduce((accumulator: { [x: string]: string; }, object: any) => {
                    let text = '';
                    if(args.lang === 'en') {
                        text = object.TXTEN;
                    } else if(args.lang === 'nl') {
                        text = object.TXTNL;
                    } else if(args.lang === 'fr') {
                        text = object.TXTFR;
                    } else if(args.lang === 'de') {
                        text = object.TXTDE;
                    }
                    accumulator[object.TXTID] = text;
                    return accumulator;
                }, {});

                return {
                    ...MSG2[args.lang],
                    ...convertedObject
                }
            }
        })


    })
})


// Slice with the real dinamic calls, made to the real server             
export const api2 = createApi({

    reducerPath: 'api2',
    baseQuery: fetchBaseQuery({
        baseUrl: import.meta.env.VITE_API2,
    }),

    tagTypes: [ 'Carts', 'Cart' ],

    endpoints: builder => ({

        getServerMaterials: builder.query<APIMaterialRoot, {lang:string,sessionId:string}>({
            query: args => {

                let params: Record<string,string> = {
                    program: 'CONF102R',
                    sessionid: args.sessionId
                };

                return {
                    url: `/cfgwebsrv.pgm`,
                    method: 'GET',
                    params
                }

            },
            transformResponse: (response: unknown, meta: FetchBaseQueryMeta, args) => {
                return transformServerMaterialsResponse(response, args.lang);
            }
        }),

        getLogin: builder.query<LoginResponse, {country:string,dealer:string|null, language:string}>({
            query: args => {

                let params: Record<string,string> = {
                    program: 'USER100R',
                    action: 'logon',
                    country: args.country,
                    language: args.language
                };

                if(args.dealer) {
                    params.mode = 'dealer',
                    params.user = args.dealer
                }


                return {
                    url: `/cfgwebsrv.pgm`,
                    method: 'GET',
                    params
                }

            }
        }),

        getMessages: builder.query<Record<string, string>, {lang: string, sessionId: string} >({            
            query: ({lang, sessionId}) => ({
                url: `/cfgwebsrv.pgm`,
                method: 'GET',
                params: {
                    program: 'CONF103R',
                    language: lang,
                    sessionid: sessionId
                }                
            }),
            transformResponse: (response: any, _, args) => {
                if(!(response).SUCCESS) {
                    throw new Error('Error retrieving messages from server API');
                }
                const convertedObject: Record<string, string> = (response as any).texts.reduce((accumulator: { [x: string]: string; }, object: { textid: string; text: any; }) => {
                    accumulator[object.textid] = object.text;
                    return accumulator;
                }, {});

                return {
                    ...MSG2[args.lang],
                    ...convertedObject
                }
            }
        }),


        getModelInfo: builder.query<Bullet[], {jrmodel: string, lang: string, sessionId: string} >({
            query: ({jrmodel, lang, sessionId}) => ({
                url: `/cfgwebsrv.pgm`,
                method:'GET',
                params: {
                    program: 'CONF100R',
                    action: 'getmodelbullets',
                    jrmodel: jrmodel,
                    language: lang,
                    sessionid: sessionId
                }                
            }),
            transformResponse: (response: any) => {
                if(response.SUCCESS) {
                    return response.bullets;
                } else {
                    throw new Error('Error retrieving model info from server API');
                }
            }
        }),

        getCustomerCarts: builder.query<CartItem[], string>({
            queryFn: async (sessionId) => {
                const result = await getCustomerCarts(sessionId);
                if(result.SUCCESS) {
                    result.shoppingcarts.sort(
                        (a:CartItem,b:CartItem) => {
                            const dateComp = -a.date.localeCompare(b.date);
                            if(dateComp === 0) {
                                return -(a.shoppingcartID - b.shoppingcartID);
                            } else {
                                return dateComp;
                            }
                        }
                    )
                    return { data: result.shoppingcarts }                    
                } else {
                    throw new Error('Error retrieving customer carts');
                }                
            },
            providesTags: [ { type: 'Carts'} ]
        }),

        getCart: builder.query<Cart, { cartId: number, sessionId: string}>({
            queryFn: async (args) => {
                const cartResponse = await getShoppingCart(args.cartId, args.sessionId);
                const basketsResponse = await getCartBaskets(args.cartId, args.sessionId);
                // TODO throwing inside map doesn't really work, change this!
                if(basketsResponse.SUCCESS) {
                    const apiBaskets = basketsResponse.shoppingbaskets;
                    const allBasketsPs = apiBaskets.map(async (apiBasket:any) => {
                        const apiLines = await getBasketLines(apiBasket.shoppingbasketID, args.sessionId);
                        if(apiLines.SUCCESS) {
                            const allLinesPs = apiLines.shoppingbasketlines.map(async (apiLine:any) => {
                                const components = await getBasketLineComponents(apiLine.shoppingbasketlineID, args.sessionId);
                                if(components.SUCCESS) {
                                    return {
                                        ...apiLine,
                                        components: components.shoppingbasketlinecomponents
                                    }
                                } else {
                                    throw new Error('Error retrieving cart content (basket line components)');
                                }
                            })
                            const lines = await Promise.all(allLinesPs);
                            return { ...apiBasket, lines };
                        } else {
                            throw new Error('Error retrieving cart content (basket lines)');
                        }
                    });
                    const baskets = await Promise.all(allBasketsPs);

                    // this is a clear mutation in a query, but the getCartBaskets is also a mutation
                    // and it was really difficult ensure that this was done after that (and it's required)
                    // so... know what you're doing before removing this.
                    
                    //const pdfResponse = await printCart(args.cartId, args.sessionId);
                    //const pdfURL = ( pdfResponse.SUCCESS && pdfResponse.pdf ) ? pdfResponse.pdf : undefined;

                    const data: Cart = {
                        name: cartResponse.name,
                        description: cartResponse.description,
                        amount: cartResponse.amount,
                        currencyCode: cartResponse.currencycode,
                        baskets 
                    };
                    return { data };
                } else {
                    throw new Error('Error retrieving cart content (baskets)')
                }
            },
            providesTags: (result, error, arg) => [ { type: 'Cart', id: arg.cartId }]
        }),

        getCart3DSaveStates: builder.query<SaveState[], { cartId: number, sessionId: string}>({
            queryFn: async (args) => {
                const basketsResponse = await getCartBaskets(args.cartId, args.sessionId);

                // outputs
                const saveStates: SaveState[] = []

                // control;
                const catalogsLoaded = new Set<string>();

                // TODO throwing inside map doesn't really work, change this!
                if(basketsResponse.SUCCESS) {
                    const apiBaskets = basketsResponse.shoppingbaskets;
                    for(const apiBasket of apiBaskets) {
                        const apiBasketDetail = await getBasket(apiBasket.shoppingbasketID, args.sessionId);
                        if(apiBasketDetail.SUCCESS && apiBasketDetail.state.trim() !== '') {
                            const basketState = JSON.parse(apiBasketDetail.state);
                            saveStates.push(basketState);                            
                        }                        
                    }
                }
                return { data: saveStates }
            },
            providesTags: (result, error, arg) => [ { type: 'Cart', id: arg.cartId }]
        })

    })
})


/**
 * Same base as api2, but with a different policy
 * (doesn't cache, fetches on any argument change)
 */
export const api3 = createApi({

    reducerPath: 'api3',
    baseQuery: fetchBaseQuery({
        baseUrl: import.meta.env.VITE_API2,
    }),
    refetchOnMountOrArgChange: true,

    endpoints: builder => ({

        getBill: builder.query<PricingResponse, PricingRequest>({
            query: (pricingRequest) => {
                const formData = makePricingRequestParams(pricingRequest);
                return {
                    url: `/cfgwebsrv.pgm`,
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    },    
                    body: formData
                }
            }
        })

    })

});

// Slice with the real dinamic calls, made to the real server             
export const drupalApi = createApi({

    reducerPath: 'drupalApi',
    baseQuery: fetchBaseQuery({
        baseUrl: import.meta.env.VITE_DRUPAL,
    }),

    endpoints: builder => ({

        getProductImagesURL: builder.query<Record<string, string>[], {lang: string} >({
            query: ({lang}) => ({
                url: `${lang}/services/configurator`,
                method:'GET',
                params: {
                    _format: "json"
                }                
            }),   
            transformResponse: (response: any) => {
                return response;
            }         
        }),

    })

})

// Export independently of the slice
export const { 
    useGetCategoriesQuery, 
    useGetFamiliesQuery, 
    useGetFamilyQuery,
    useGetVariantsQuery, 
    useGetOptionsQuery, 
    useGetCommonElementsQuery,
    useGetElementsQuery, 
    useGetMaterialsQuery, 
    useGetPartsQuery,
    useGetFamilyPartsQuery,

} = api;

export const {
    
    useGetServerMaterialsQuery,    
    useGetLoginQuery,
    useGetMessagesQuery,
    useGetModelInfoQuery,

    useGetCustomerCartsQuery,
    useGetCartQuery,
    useGetCart3DSaveStatesQuery

} = api2;

export const {
    useGetBillQuery
} = api3;

export const {    
    useGetProductImagesURLQuery,
} = drupalApi;
 



